import { useState } from 'react';
import dayjs from 'dayjs';
import './graph.scss';

import { ScaleValueProperties, ScaleYValueProperties } from './types';
import {
	DEFAULT_SCALE_X,
	GRAPH_CONSTANTS,
	LabelPeriods,
	LabelPeriodsYFrequency,
	Periods,
	intervalData,
} from './constants';
import { useChartOptions, useGraphAnnotations, useGraphData } from './hooks';
import LineChart from './lineChart';
import GraphControlsPanel from './graphControlsPanel';
import Annotations from './Annotations/annotations';
import { BehaviorGraphNotes, TargetGraphNotes } from './graphNotes';

import { useSelector } from '../../redux/store';
import { TargetTypes } from '../../redux/API/ClientAPIHelpers/programTargetsProperties';
import { BehaviorReductionProperties } from '../../redux/API/ClientAPIHelpers/behaviorReductionProperties';

export interface TargetData {
	name: string;
	type: TargetTypes.DTT | TargetTypes.TA;
}

interface BaseGraphContentProperties {
	graphType: 'target' | 'behavior';
	id: string;
}

interface TargetGraphContentProperties extends BaseGraphContentProperties {
	data: TargetData;
	graphType: 'target';
}

interface BehaviorGraphContentProperties extends BaseGraphContentProperties {
	data: BehaviorReductionProperties;
	graphType: 'behavior';
}

type GraphContentProperties = TargetGraphContentProperties | BehaviorGraphContentProperties;

const GraphContent = (props: GraphContentProperties) => {
	const { graphType, id, data } = props;

	// Extract values based on graphType
	const name = data.name;
	const subType: TargetTypes.DTT | TargetTypes.TA | 'Frequency' | 'Duration' =
		graphType === 'target' ? data.type : (data.recordingType.text as 'Frequency' | 'Duration');

	const clientInfo = useSelector((state) => state.client.client);

	// Derive title based on graphType
	const title =
		graphType === 'target'
			? data.name
			: (() => {
					// eslint-disable-next-line
					const { name, category, recordingType } = data;
					const totalText =
						recordingType.text === 'Frequency' ? 'Summary' : recordingType.text === 'Duration' ? 'Total' : '';

					return `${clientInfo.fullName} ${name} ${totalText} ${recordingType.text} ${category.name}`;
				})();

	// Derive yAxisLabel based on graphType
	const yAxisLabel =
		graphType === 'target'
			? data.type === TargetTypes.DTT
				? '% Correct Responding'
				: '% Steps Correct'
			: data.recordingType.text === 'Frequency'
				? 'Daily Frequency'
				: 'Duration (min)';

	// Derive suggestedMax based on graphType
	const suggestedMax =
		graphType === 'target' ? GRAPH_CONSTANTS.SUGGESTED_MAX_PERCENTAGE : GRAPH_CONSTANTS.SUGGESTED_MAX_DEFAULT;

	const today = dayjs();
	const todayString = today.format(GRAPH_CONSTANTS.DATE_FORMAT);
	const threeMonthPast = today.subtract(3, 'month').format(GRAPH_CONSTANTS.DATE_FORMAT);

	const [selectValue, setSelectValue] = useState<string>(GRAPH_CONSTANTS.DEFAULT_SELECT_VALUE);
	const [activeInterval, setActiveInterval] = useState<string>(GRAPH_CONSTANTS.DEFAULT_INTERVAL);
	const [periodicity, setPeriodicity] = useState<string>('');
	const [startDate, setStartDate] = useState<string>(threeMonthPast);

	const annotationsList = useSelector((state) =>
		graphType === 'target' ? state.graphAnnotation.targetAnnotations : state.graphAnnotation.behaviorAnnotations,
	);

	const [scaleX, setScaleX] = useState<ScaleValueProperties>(DEFAULT_SCALE_X);

	const [scaleY, setScaleY] = useState<ScaleYValueProperties>({
		title: {
			display: true,
			text: yAxisLabel,
		},
		suggestedMin: GRAPH_CONSTANTS.SUGGESTED_MIN,
		suggestedMax,
	});

	const { displayData } = useGraphData({
		id,
		startDate,
		endDate: todayString,
		periodicity,
		graphType,
		subType,
	});

	const { chartAnnotations, filteredAnnotations } = useGraphAnnotations({
		id,
		graphType,
		annotationsList,
		startDate,
	});

	const chartOptions = useChartOptions({
		title,
		annotations: chartAnnotations,
	});

	const handleIntervalChange = (interval: string) => {
		setActiveInterval(interval);
		const date = intervalData.find((x) => x.id === interval);
		if (!date) return;
		setStartDate(date.value);
	};

	const selectValueHandler = (value: string) => {
		setSelectValue(value);

		const unitValue = parseInt(value);
		setPeriodicity(`${unitValue + 1}`);

		const newScaleX: ScaleValueProperties = {
			type: 'time',
			time: {
				unit: Periods[unitValue],
			},
			title: {
				display: true,
				text: LabelPeriods[unitValue],
			},
		};
		setScaleX(newScaleX);

		if (graphType === 'behavior' && subType === 'Frequency') {
			const newScaleY: ScaleYValueProperties = {
				title: {
					display: true,
					text: LabelPeriodsYFrequency[unitValue],
				},
				suggestedMin: GRAPH_CONSTANTS.SUGGESTED_MIN,
				suggestedMax: GRAPH_CONSTANTS.SUGGESTED_MAX_DEFAULT,
			};
			setScaleY(newScaleY);
		}
	};

	return (
		<div className="graphContainer">
			<Annotations id={id} name={name} type={graphType} clientId={clientInfo.id} />

			<LineChart
				displayData={displayData}
				scaleX={scaleX}
				scaleY={scaleY}
				startDate={startDate}
				options={chartOptions}
			/>

			<LineChart
				displayData={displayData}
				scaleX={scaleX}
				scaleY={scaleY}
				startDate={startDate}
				options={chartOptions}
				isDownloadVersion
			/>

			<GraphControlsPanel
				activeInterval={activeInterval}
				selectValue={selectValue}
				onIntervalChange={handleIntervalChange}
				onSelectChange={selectValueHandler}
				clientName={clientInfo.fullName ?? ''}
				itemName={name}
				graphType={graphType}
				annotationsList={filteredAnnotations}
			/>

			{graphType === 'target' ? <TargetGraphNotes targetId={id} /> : <BehaviorGraphNotes behaviorId={id} />}
		</div>
	);
};

export default GraphContent;
